import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, nextTick } from "vue";
import elasticlunr, { SearchResults, Index } from 'elasticlunr';
import Loader from "./loader.vue";
import { debounce } from "lodash-es";
import { useOrganizations } from "@/store/organizations";
import { useAuth } from "@/store/auth"
import { type Organizations, type DataInstance } from "@/platform-data";
import {
	filtersBase,
	filterOrganizations,
	longNameClass,
	prettifyURLs,
	// type SearchableFields,
	type SelectedFilters,
} from "@/util/chart-utils";
import { useGoogleMaps } from "@/store/googleMaps";
import FilterSection from "@/components/filter-section.vue"

type Emits = {
	(e: "moveToLogin", from:string):void,
	(e: "detailView", id: string):void,
}

type Props = {
	isMobile: boolean | string,
}

const DEBOUNCE_DURATION = 1000;
const pageSize = 25;


export default /*#__PURE__*/_defineComponent({
  __name: 'database-tool',
  props: ["isMobile"],
  emits: ["moveToLogin", "detailView"],
  setup(__props, { expose: __expose, emit }) {
  __expose();

const props = __props;







const auth = useAuth();
const { selectedOrganization, waitForReady, selectOrganization, setOrgDetails } = useGoogleMaps();
const organizations = useOrganizations();

const currentPage = ref(1);
const searchQuery = ref("");
const showFilters = ref(true);
const showLocation = ref(false);
const orgsCountTotal = ref(0);
const loading = ref<boolean>(false);
const activeSearchRef = ref<SearchResults[]>();
const selectedFilters = ref<SelectedFilters>({...filtersBase});

/* ----- Device type dependant layout/rendering ----- */

const deviceType = computed(() => {
	let width = Math.round(document.getElementById("database-tool")?.getBoundingClientRect().width || 656);
	let type = width > 654 ? "desktop" : "mobile";
	return type;
});

const filterColumnClass = ref("col")

/* ----- Pagination ----- */
function changePage(input: string | number = "inc"){
	if (typeof input === "number") {
		currentPage.value = input;
	} else if (input === "inc") {
		currentPage.value = Math.min(currentPage.value + 1, totalPages.value);
	} else if (input === "dec") {
		currentPage.value = Math.max(currentPage.value - 1, 1);
	}
}

// Computed property to paginate the filtered organizations
const paginatedOrganizations = computed(() => {
	const start = (currentPage.value - 1) * pageSize;
	const end = start + pageSize;

	const sortedOrganizations = filteredOrganizations.value
		.sort((a, b) => a['Organization Name'].localeCompare(b['Organization Name']));

	return sortedOrganizations.slice(start, end);
});

const totalPages = computed(() => {
	return Math.ceil(Object.values(filteredOrganizations.value).length / pageSize);
});

const visiblePages = computed(() => {
	const maxPagesToShow = 3;
	let start = Math.max(1, currentPage.value - 1);
	let end = Math.min(totalPages.value, start + maxPagesToShow - 1);

	// Adjust start if we're at the end of the list
	if (end - start + 1 < maxPagesToShow) {
		start = Math.max(1, end - maxPagesToShow + 1);
	}

	const pages = [];
	for (let i = start; i <= end; i++) {
		pages.push(i);
	}
	return pages;
});

const loadMore = () => {
	currentPage.value += 1;
}

/*----- Search function -----*/

const index = ref<Index< any >>(elasticlunr(function () {
	this.setRef('id');
	this.addField('Executive Email');
	this.addField('HQ Street Address');
	this.addField('Organization Name');
	this.addField('Products / Services');
}));

const debouncedSearch = debounce(() => {
  console.log("Debounced search... Search Query : ", searchQuery.value);
  const query = searchQuery.value.trim();

  // Try to find exact matches for organization name
  const exactMatches = index.value.search(query, {
    fields: {
      'Organization Name': { boost: 10 },  // Boost for exact match
	  'Executive Email': { boost: 10 },
	  'HQ Street Address': { boost:10 },
	  'Products / Services': { boost: 10 }
    },
    bool: "AND", // Exact match required for all fields
  });

  if (exactMatches.length > 0) {
    // If there's an exact match, only return those results
    activeSearchRef.value = exactMatches;
  } else {
    // Fallback to partial matching on all fields
    activeSearchRef.value = index.value.search(query, {
      fields: {
        'Organization Name': { boost: 4 },
        'Executive Email': { boost: 1 },
        'HQ Street Address': { boost: 3 },
        'Products / Services': { boost: 2 }
      },
      bool: "OR",  // Partial matches allowed
    });
  }
  loading.value = false;
}, DEBOUNCE_DURATION);


const trySearch = ()=>{
	loading.value = true;
	debouncedSearch();
}

const addOrgToIndex = (org: DataInstance<Organizations.Model>) => {
	const doc = {
		"id": org.$id,
		"Executive Email": org['Executive Email'] || "",
		"HQ Street Address": org['HQ Street Address'] || "",
		"Organization Name": org['Organization Name'] || "",
		"Products / Services": org['Products / Services'] || ""
	}
	index.value.addDoc(doc);
};

watch(()=> searchQuery.value, ()=>{
	trySearch();
})

/*----- Filters -----*/

/** On click "Clear All" button */
function clearFilters(){
	showFilters.value = false;
	selectedFilters.value = filtersBase;
	searchQuery.value = "";
}

function handleFilterSelection(filterName: string, selectedOptions: string[]) {
	const filterKey = filterName.replace(' ', '') as keyof typeof selectedFilters.value;
	// Update the selected filters with the entire array of selected options
	selectedFilters.value[filterKey] = [...selectedOptions];
}

function onSectionClick (isType:string="") {
	if(isType==="filters"){showFilters.value=!showFilters.value;}
}

const filterViaSearchRef = (org: DataInstance<Organizations.Model>) => {
  return activeSearchRef.value?.some(s => s.ref === org.$id) || searchQuery.value.length === 0;
};

const filteredOrganizations = computed(() => {
	let filteredList = Object.values(organizations.organizations);
	filteredList = filteredList.filter(filterViaSearchRef);
	filteredList = filterOrganizations(selectedFilters.value, filteredList);
	orgsCountTotal.value = filteredList.length;
	return filteredList;
});

onMounted(async () => {
	await organizations.waitForReady();
	await waitForReady();
	await auth.waitForReady();
	if(auth.user === null){
		emit("moveToLogin","database")
	}
	Object.values(organizations.organizations).forEach((org) => {
		addOrgToIndex(org);
	});
	orgsCountTotal.value = filteredOrganizations.value.length;
	if(selectedOrganization){
		searchQuery.value = selectedOrganization
		selectOrganization(null);
	}
	const hashValue = window.location.hash.substring(1); // Remove the leading '#'
	if(hashValue){
		const decodedHash = decodeURIComponent(hashValue);
		searchQuery.value = decodedHash;
	}
	trySearch();  // Run the search immediately on mount if there's a query

	showFilters.value = props.isMobile ? false : true;
});


const __returned__ = { props, emit, auth, selectedOrganization, waitForReady, selectOrganization, setOrgDetails, organizations, DEBOUNCE_DURATION, pageSize, currentPage, searchQuery, showFilters, showLocation, orgsCountTotal, loading, activeSearchRef, selectedFilters, deviceType, filterColumnClass, changePage, paginatedOrganizations, totalPages, visiblePages, loadMore, index, debouncedSearch, trySearch, addOrgToIndex, clearFilters, handleFilterSelection, onSectionClick, filterViaSearchRef, filteredOrganizations, ref, computed, watch, onMounted, nextTick, get elasticlunr() { return elasticlunr }, get SearchResults() { return SearchResults }, get Index() { return Index }, Loader, get debounce() { return debounce }, get useOrganizations() { return useOrganizations }, get useAuth() { return useAuth }, get filtersBase() { return filtersBase }, get filterOrganizations() { return filterOrganizations }, get longNameClass() { return longNameClass }, get prettifyURLs() { return prettifyURLs }, get useGoogleMaps() { return useGoogleMaps }, FilterSection }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})